import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { graphql, useMutation } from "react-relay";
import { useSignupPageQuery } from "./loaders/SignupPage";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useIntl, FormattedMessage } from "react-intl";
import * as v from "valibot";
import { valibotResolver } from "@hookform/resolvers/valibot";

import { SignupPageEmailMutation as SignupPageEmailMutationType } from "./__generated__/SignupPageEmailMutation.graphql";

import { Button } from "@/kit/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import { PasswordToggleIcon } from "@/kit/ui/password-toggle";
import { Mail01Icon, Lock01Icon } from "@/icons";
import { Link } from "@/kit/ui/link";

import LogoIcon from "@/components/LogoIcon";
import {
  AuthHeader,
  AuthHeaderTitle,
  AuthHeaderIcon,
  AuthHeaderDescription,
  AuthContent,
} from "@/components/AuthLayout";
import Oauth2LoginButtons from "@/components/Oauth2LoginButtons";

import { relayErrorMessage } from "@/utils/relay";
import { toast } from "@/utils/toast";
import { logger } from "@/common/logger";
import { MetaLayout } from "@/common/MetaLayout";

const SignupPageEmailMutation = graphql`
  mutation SignupPageEmailMutation($input: SendEmailSignupVerificationInput!) {
    sendEmailSignupVerification(input: $input) {
      id
    }
  }
`;

export default function Signup() {
  const { query } = useSignupPageQuery();
  const navigate = useNavigate();
  const intl = useIntl();
  const [searchParams] = useSearchParams();

  const [commitMutation, isMutationInFlight] =
    useMutation<SignupPageEmailMutationType>(SignupPageEmailMutation);

  const formSchema = useMemo(
    () =>
      v.object({
        email: v.pipe(
          v.string(),
          v.email(
            intl.formatMessage({
              defaultMessage: "Invalid email address",
            }),
          ),
        ),
        password: v.pipe(
          v.string(),
          v.minLength(
            6,
            intl.formatMessage({
              defaultMessage: "Password must be at least 6 characters",
            }),
          ),
        ),
      }),
    [intl],
  );
  const form = useForm<v.InferOutput<typeof formSchema>>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: valibotResolver(formSchema),
  });

  const onSubmit = (data: v.InferOutput<typeof formSchema>) => {
    commitMutation({
      variables: {
        input: {
          agreeToTermsAndPrivacyPolicy: true,
          ...data,
        },
      },
      onError(error) {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
      onCompleted({ sendEmailSignupVerification: { id } }) {
        navigate(
          {
            pathname: `/verify-email-signup/${id}`,
            search: searchParams.toString(),
          },
          {
            state: { email: data.email },
          },
        );
      },
    });
  };
  return (
    <MetaLayout
      metaTitle={intl.formatMessage({
        defaultMessage:
          "Sign Up on Aqora.io - Join the Global Quantum Community",
      })}
    >
      <AuthHeader>
        <AuthHeaderIcon>
          <LogoIcon />
        </AuthHeaderIcon>
        <AuthHeaderTitle>
          <FormattedMessage defaultMessage="Welcome to Aqora" />
        </AuthHeaderTitle>
        <AuthHeaderDescription>
          <FormattedMessage defaultMessage="Join the community of quantum innovators" />
        </AuthHeaderDescription>
      </AuthHeader>
      <AuthContent>
        <Form form={form} onSubmit={onSubmit}>
          <div className="space-y-8">
            <div className="space-y-6">
              <Oauth2LoginButtons query={query} />
              <div className="space-y-5">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage defaultMessage="Email" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          autoFocus
                          startDecorator={<Mail01Icon />}
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage defaultMessage="Password" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          startDecorator={<Lock01Icon />}
                          endDecorator={<PasswordToggleIcon />}
                          type="password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <p className="text-grey-600 text-center text-sm">
                  <FormattedMessage
                    defaultMessage="By continuing you agree to the <terms>Terms & Conditions</terms> and the <privacy>Privacy Policy</privacy>."
                    values={{
                      terms: (chunks) => (
                        <Link
                          to={"https://aqora.io/terms"}
                          target="_blank"
                          className="text-gery-600 font-normal underline"
                        >
                          {chunks}
                        </Link>
                      ),
                      privacy: (chunks) => (
                        <Link
                          to={"https://aqora.io/privacy-policy"}
                          target="_blank"
                          className="text-gery-600 font-normal underline"
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                </p>
              </div>
            </div>
            <Button
              className="w-full"
              type="submit"
              disabled={isMutationInFlight}
            >
              <FormattedMessage defaultMessage="Continue" />
            </Button>
            <p className="text-center">
              <FormattedMessage defaultMessage="Already have an account?" />{" "}
              <Link
                to={{ pathname: "/login", search: searchParams.toString() }}
              >
                <FormattedMessage defaultMessage="Login" />
              </Link>
            </p>
          </div>
        </Form>
      </AuthContent>
    </MetaLayout>
  );
}
